import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'

interface VersionInfo {
  version: string
  extension_id: string
  build_time: string
  files: {
    crx: { url: string; size: number; content_type: string }
    zip: { url: string; size: number; content_type: string }
    updates_xml: { url: string }
  }
  requirements: {
    browser: string
    developer_mode: boolean
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(0)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function ExtensionDownload() {
  const [version, setVersion] = useState<VersionInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL || 'https://api.personicai.com'}/extension/version`)
      .then(res => {
        if (!res.ok) throw new Error('Version info not available')
        return res.json()
      })
      .then(setVersion)
      .catch(() => setError('Extension release not available yet.'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Chrome Extension</h2>
        <p className="text-gray-600 mt-1">
          Download Personic Identity for Chrome to bring your personality profile everywhere you go.
        </p>
      </div>

      {/* Hero card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="card p-8 relative overflow-hidden"
      >
        {/* Subtle accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-copper-400 via-personic-500 to-copper-400" />

        <div className="flex items-start gap-6">
          {/* Extension icon */}
          <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-midnight-800 to-midnight-950 flex items-center justify-center shadow-lg">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-copper-400">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-semibold text-midnight-900">Personic Identity</h3>
              {version && (
                <span className="badge badge-copper text-xs">v{version.version}</span>
              )}
            </div>
            <p className="text-gray-600 mt-1 text-sm leading-relaxed">
              Passive profile building, companion personas, and real-time voice copilot
              across Claude, ChatGPT, Gemini, and Perplexity.
            </p>

            {loading ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading version info...
              </div>
            ) : error ? (
              <div className="mt-4 p-3 bg-warm-100 border border-warm-200 rounded-lg text-sm text-warm-700">
                {error}
              </div>
            ) : version ? (
              <div className="mt-5 flex items-center gap-3 flex-wrap">
                <a
                  href={version.files.crx.url}
                  className="btn btn-primary inline-flex items-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download .crx
                  <span className="text-xs opacity-75">({formatBytes(version.files.crx.size)})</span>
                </a>
                <a
                  href={version.files.zip.url}
                  className="btn btn-secondary inline-flex items-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download .zip
                  <span className="text-xs opacity-75">({formatBytes(version.files.zip.size)})</span>
                </a>
              </div>
            ) : null}

            {version && (
              <p className="text-xs text-gray-400 mt-3">
                Built {formatDate(version.build_time)}
              </p>
            )}
          </div>
        </div>
      </motion.div>

      {/* Installation instructions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Method 1: CRX */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
          className="card p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="badge badge-copper text-xs">Recommended</span>
            <h4 className="font-semibold text-midnight-900">CRX Install</h4>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Signed package with automatic updates. Chrome checks for new versions every few hours.
          </p>
          <ol className="space-y-3 text-sm text-gray-700">
            <Step n={1}>Download the <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">.crx</code> file above</Step>
            <Step n={2}>Open <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">chrome://extensions</code> in your browser</Step>
            <Step n={3}>Enable <strong>Developer Mode</strong> (toggle in top-right corner)</Step>
            <Step n={4}>Drag and drop the <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">.crx</code> file onto the extensions page</Step>
            <Step n={5}>Click <strong>Add extension</strong> when prompted</Step>
          </ol>
        </motion.div>

        {/* Method 2: ZIP */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15 }}
          className="card p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="badge badge-midnight text-xs">Alternative</span>
            <h4 className="font-semibold text-midnight-900">ZIP / Load Unpacked</h4>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Manual install without auto-updates. You'll need to re-download when new versions are released.
          </p>
          <ol className="space-y-3 text-sm text-gray-700">
            <Step n={1}>Download the <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">.zip</code> file and extract it to a permanent folder</Step>
            <Step n={2}>Open <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">chrome://extensions</code> in your browser</Step>
            <Step n={3}>Enable <strong>Developer Mode</strong> (toggle in top-right corner)</Step>
            <Step n={4}>Click <strong>Load unpacked</strong> and select the extracted folder</Step>
          </ol>
        </motion.div>
      </div>

      {/* Info rows */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.2 }}
        className="card p-6"
      >
        <h4 className="font-semibold text-midnight-900 mb-4">Requirements & Compatibility</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wide mb-1.5">Browser</p>
            <p className="text-gray-800">Chrome 120+ or any Chromium-based browser (Edge, Brave, Arc)</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wide mb-1.5">Developer Mode</p>
            <p className="text-gray-800">Required for sideloaded extensions (both install methods)</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wide mb-1.5">Supported Platforms</p>
            <div className="flex flex-wrap gap-1.5 mt-0.5">
              {['Claude', 'ChatGPT', 'Gemini', 'Perplexity'].map(p => (
                <span key={p} className="badge text-xs">{p}</span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wide mb-1.5">Voice Copilot</p>
            <div className="flex flex-wrap gap-1.5 mt-0.5">
              {['Google Meet', 'Zoom', 'Teams'].map(p => (
                <span key={p} className="badge text-xs">{p}</span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Auto-update note */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.25 }}
        className="p-4 bg-personic-50 border border-personic-200 rounded-xl text-sm text-personic-800 flex items-start gap-3"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 mt-0.5 text-personic-500">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <div>
          <strong>Auto-updates:</strong> CRX installs receive automatic updates when new versions are published.
          Chrome checks for updates every few hours. ZIP/load-unpacked installs do not auto-update
          and must be manually re-downloaded. A "not from Chrome Web Store" warning is normal for
          sideloaded extensions.
        </div>
      </motion.div>
    </div>
  )
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-midnight-800 text-white text-xs flex items-center justify-center font-medium mt-0.5">
        {n}
      </span>
      <span>{children}</span>
    </li>
  )
}
